<template>
    <div>
        <div v-if="loading" class="container text-center py-5">
            <span class="fs-5 open-sans">Loading...</span>
        </div>

        <div v-else-if="error || !post" class="container text-center py-5">
            <span class="fs-5 open-sans text-danger">Post not found.</span>
            <div class="mt-3">
                <router-link to="/news" class="dark-btn btn px-4 text-uppercase">Back to News</router-link>
            </div>
        </div>

        <div v-else>
            <!-- Hero image -->
            <div v-if="post.cover_image" class="news-hero">
                <img :src="`${$API_ASSETS}${post.cover_image.id}?width=1600`"
                    class="news-hero-img" alt="" />
            </div>

            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-lg-8 col-md-10 py-5">

                        <!-- Back link -->
                        <router-link to="/news" class="news-back open-sans d-inline-flex align-items-center mb-4">
                            <i class="fa-solid fa-arrow-left me-2"></i> Back to News
                        </router-link>

                        <!-- Meta -->
                        <div class="d-flex align-items-center gap-3 mb-4 flex-wrap">
                            <div v-if="post.author" class="d-flex align-items-center gap-3">
                                <img v-if="post.author.headshot"
                                    :src="`${$API_ASSETS}${post.author.headshot.id}.png?width=160&height=160&fit=cover`"
                                    class="detail-avatar" alt="" />
                                <div v-else class="detail-avatar-placeholder"></div>
                                <div class="d-flex flex-column">
                                    <span class="detail-author-label open-sans">Written by</span>
                                    <span class="open-sans-500 detail-author-name">{{ post.author.name }}</span>
                                    <span class="news-date open-sans mt-1">{{ formatDate(post.date_published) }}</span>
                                </div>
                            </div>
                            <span v-else class="news-date open-sans">{{ formatDate(post.date_published) }}</span>
                        </div>

                        <!-- Title -->
                        <h1 class="oswald-bold text-uppercase news-detail-title mb-4">{{ post.title }}</h1>

                        <!-- Excerpt -->
                        <p v-if="post.excerpt" class="news-detail-excerpt open-sans mb-4">{{ post.excerpt }}</p>

                        <!-- Body content -->
                        <div class="news-detail-content open-sans" v-html="post.content"></div>

                        <!-- Bottom back link -->
                        <div class="mt-5 pt-3 border-top">
                            <router-link to="/news" class="dark-btn btn px-4 text-uppercase">
                                <i class="fa-solid fa-arrow-left me-2"></i> Back to News
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Footer />
    </div>
</template>

<script>
import axios from 'axios';
import Footer from '@/components/Footer.vue';

const SITE_URL = 'https://atlasimprov.com';
const ASSETS   = 'https://api.atlasimprov.com/assets/';

function setMeta(attr, key, value) {
    if (value === null || value === undefined) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', value);
}

function setLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

export default {
    name: 'NewsDetail',
    components: { Footer },
    data() {
        return {
            post: null,
            loading: true,
            error: false,
        };
    },
    methods: {
        applyHead(post) {
            const title = `Atlas Improv – ${post.title}`;
            const desc  = post.excerpt || '';
            const url   = `${SITE_URL}/news/${post.slug}`;
            const image = post.cover_image
                ? `${ASSETS}${post.cover_image.id}?width=1200&height=630&fit=cover`
                : `${SITE_URL}/social_card_1200x630.png`;

            document.title = title;
            setMeta('name',     'description',         desc);
            setLink('canonical', url);

            setMeta('property', 'og:type',         'article');
            setMeta('property', 'og:title',        post.title);
            setMeta('property', 'og:description',  desc);
            setMeta('property', 'og:image',        image);
            setMeta('property', 'og:url',          url);

            setMeta('name', 'twitter:card',        'summary_large_image');
            setMeta('name', 'twitter:title',       post.title);
            setMeta('name', 'twitter:description', desc);
            setMeta('name', 'twitter:image',       image);

            if (post.author?.name) setMeta('name', 'author', post.author.name);
            if (post.date_published) setMeta('property', 'article:published_time', post.date_published);
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        },
        async fetchPost(slug) {
            try {
                const response = await axios.post(this.$API_GRAPHQL, {
                    query: `
                        query {
                            news_post(filter: { slug: { _eq: "${slug}" } }) {
                                id
                                title
                                slug
                                excerpt
                                content
                                date_published
                                cover_image { id }
                                author {
                                    id
                                    name
                                    headshot { id }
                                }
                            }
                        }
                    `
                }, {
                    headers: {
                        'Authorization': `Bearer Pm4Nd5_e46HaK2xtkPcla2PMBzbk9AQf`,
                        'Content-Type': 'application/json'
                    }
                });
                const results = response.data.data.news_post;
                this.post = (results && results.length > 0) ? results[0] : null;
                if (this.post) {
                    this.applyHead(this.post);
                }
            } catch (e) {
                console.error('Error fetching post:', e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        }
    },
    created() {
        this.fetchPost(this.$route.params.slug);
    },
    watch: {
        '$route.params.slug'(newSlug) {
            this.loading = true;
            this.error = false;
            this.post = null;
            this.fetchPost(newSlug);
        }
    }
};
</script>

<style scoped>
.news-hero {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
    display: flex;
    justify-content: center;
    background: #000;
}

.news-hero-img {
    max-width: 100%;
    max-height: 600px;
    height: auto;
    width: auto;
    object-fit: contain;
    display: block;
}

.news-back {
    color: var(--atlas-blue);
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: color 0.2s;
}

.news-back:hover {
    color: var(--site-black);
}

.news-date {
    font-size: 0.85rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.detail-avatar {
    width: 110px;
    height: 110px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    border: 3px solid #eee;
}

.detail-avatar-placeholder {
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: #ddd;
    flex-shrink: 0;
}

.detail-author-label {
    font-size: 0.72rem;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.detail-author-name {
    font-size: 1.05rem;
    color: var(--site-black);
    line-height: 1.2;
}

.news-detail-title {
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 1.1;
    color: var(--site-black);
}

.news-detail-excerpt {
    font-size: 1.15rem;
    color: #444;
    line-height: 1.7;
    border-left: 3px solid var(--atlas-blue);
    padding-left: 1rem;
    font-style: italic;
}

.news-detail-content {
    font-size: 1rem;
    color: #333;
    line-height: 1.8;
}

.news-detail-content :deep(h2),
.news-detail-content :deep(h3) {
    font-family: 'Oswald', sans-serif;
    text-transform: uppercase;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
}

.news-detail-content :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1rem 0;
}

.news-detail-content :deep(a) {
    color: var(--atlas-blue);
}

.news-detail-content :deep(blockquote) {
    border-left: 3px solid var(--atlas-yellow);
    padding-left: 1rem;
    color: #555;
    font-style: italic;
    margin: 1.5rem 0;
}
</style>
